import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../components/RenderWithTooltip";
import StatusChip from "../../../../components/StatusChip";
import Text from "../../../../components/Text";
import ActionMenu from "../../../../components/app/ActionMenu";
import DeleteModal from "../../../../components/app/DeleteModal";
import AppointmentTypeDrawer from "../../../../components/app/admin/provider-groups/AppointmentTypeDrawer";
import { AppointmentTypeType } from "../../../../constants/ProviderGroupFormInitialValues";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useArchiveState } from "../../../../hooks/useArchiveState";
import { useArchiveAppointmentType } from "../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-archive";
import { useAppointmentTypeGetAll } from "../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DEFAULT_UUID_NAME_LIST,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { INTAKE_FORM_SECTIONS } from "../../provider/scheduling/appointment/appointment-constant";
import { getBaseRole } from "@/utils/auth-storage";

const ProviderGroupAppoinmentType = () => {
  const params = useParams();

  const {
    searchKey,
    showArchivedAppointmentType,
  }: {
    searchKey: string;
    stateFilter: string;
    showArchivedAppointmentType: boolean;
    setShowArchivedAppointmentType: (value: boolean) => void;
  } = useOutletContext<{
    searchKey: string;
    stateFilter: string;
    showArchivedAppointmentType: boolean;
    setShowArchivedAppointmentType: (value: boolean) => void;
  }>();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const {
    data: ProviderGroupDepartmentList,
    refetch,
    isLoading: tableDataLoading,
    isFetching,
  } = useAppointmentTypeGetAll({
    gridSorting,
    pagination,
    searchString: searchKey,
    status: true,
    archive: showArchivedAppointmentType,
    xTENANTID: getXtenantId(),
  });

  const [archive, setArchive] = useState(false);
  const { mutateAsync: archiveAppointmentType } = useArchiveAppointmentType();
  const { deleteDisc, archiveHandler } = useArchiveState(
    archiveAppointmentType
  );
  const upsertAppointmentTypeDisclosure = useDisclosure();

  const isPracticeAdmin = getBaseRole() === "PRACTICE_ADMIN";

  const COLUMNS = applyDefaultColumnSettings<AppointmentTypeType>([
    {
      field: "typeName",
      // headerAlign: "center",
      flex: 1,
      minWidth: 250,
      headerName: "Appointment Type",
      renderCell: ({ value }) => {
        return (
          <RenderWithTooltip
            value={replaceUnderScoreAddSpace(value?.toLowerCase()) ?? "-"}
          />
        );
      },
    },
    {
      field: "duration",
      // headerAlign: "center",
      flex: 1,
      minWidth: 250,
      headerName: "Duration (Mins)",
      renderCell: ({ value }) => {
        return <Text isTabletext>{value || "-"}</Text>;
      },
    },
    // {
    //   field: "color",
    //   // headerAlign: "center",
    //   flex: 1,
    //   minWidth: 250,
    //   headerName: "Calendar Color",
    //   renderCell: ({ value }) => {
    //     return (
    //       <Flex gap={1}>
    //         <Box
    //           sx={{
    //             width: "30px",
    //             height: "15px",
    //             backgroundColor: value,
    //             borderRadius: "2px",
    //             display: "inline-block",
    //           }}
    //         />
    //         <Text>{value ?? "-"}</Text>
    //       </Flex>
    //     );
    //   },
    // },
    {
      field: "archive",
      headerName: "Status",
      minWidth: 200,
      renderCell: ({ value }) => {
        const isActive = !Boolean(value);
        return <StatusChip value={isActive} />;
      },
    },
    {
      field: "Actions",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }: any) => {
        return (
          <ActionMenu
            options={[
              {
                value: "edit",
                label: "Edit",
                isDisabled: row?.archive,
                hoverMsg: "Unarchive this appointment type to edit",
              },
              {
                value: "archive",
                label: row?.archive ? "Unarchive" : "Archive",
              },
            ]}
            onActionClick={(v) => {
              if (v === "edit") {
                const payload = {
                  ...row,
                  workLocations: transformMultiSelectFrontEndVal(
                    DEFAULT_UUID_NAME_LIST,
                    row.workLocations
                  ),
                  intakeForms: row.intakeForms
                    ? row.intakeForms.split(",").map((val: string) => {
                        const trimmed = val.trim();
                        const match = INTAKE_FORM_SECTIONS.find(
                          (item) => item.value === trimmed
                        );
                        return {
                          uuid: trimmed,
                          name: match?.label || trimmed,
                        };
                      })
                    : undefined,
                };
                upsertAppointmentTypeDisclosure.open(payload);
              } else if (v === "archive") {
                setArchive(row?.archive);
                deleteDisc.open(row?.uuid);
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <Box width={"100%"}>
      <AppointmentTypeDrawer
        disclosureHost={upsertAppointmentTypeDisclosure}
        modalTitle={"Edit Appointment Type"}
      />

      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archive, id, params?.providerGroupId).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${
          archive ? "unarchive" : "archive"
        } this Appointment Type`}
      />
      <Box height={"calc(100vh - 160px)"}>
        <CustomDataGridV2
          loading={tableDataLoading || isFetching}
          searchString={searchKey}
          getRowId={(row) => row.uuid}
          rows={ProviderGroupDepartmentList?.data?.content ?? []}
          rowCount={ProviderGroupDepartmentList?.data?.page?.totalElements ?? 0}
          columns={
            isPracticeAdmin
              ? COLUMNS
              : COLUMNS.filter((column) => column.field !== "Action")
          }
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
        />
      </Box>
    </Box>
  );
};

export default ProviderGroupAppoinmentType;
